import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  getSetlist, moveItem, removeItem, type Setlist as List, type SetlistItem,
  setItemTranspose, shareSetlist,
} from "../lib/setlists";
import { shift } from "../lib/chords";

export function Setlist() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const shareToken = params.get("k");
  const [list, setList] = useState<List | null>(null);
  const [items, setItems] = useState<SetlistItem[]>([]);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  useEffect(() => {
    getSetlist(id, shareToken).then(({ list, items }) => { setList(list); setItems(items); });
  }, [id]);

  const minutes = Math.round(
    items.reduce((total, item) => total + (item.songs?.duration ?? 0), 0) / 60);

  async function move(index: number, direction: -1 | 1) {
    setItems(await moveItem(items, index, direction));
  }

  async function transposeItem(item: SetlistItem, delta: number) {
    const next = item.transpose + delta;
    setItems(items.map((row) =>
      row.position === item.position ? { ...row, transpose: next } : row));
    await setItemTranspose(id, item.position, next);
  }

  if (!list) return <div className="page"><p className="muted">Loading…</p></div>;

  return (
    <div className="page">
      <header className="song-head">
        <div>
          <h1 className="serif song-title" style={{ fontSize: 34 }}>{list.name}</h1>
          <p className="muted" style={{ margin: "8px 0 0" }}>
            {items.length} songs · {minutes} minutes
            {list.venue ? ` · ${list.venue}` : ""}
          </p>
        </div>
        <div className="controls no-print" style={{ margin: 0 }}>
          <button className="btn" onClick={async () => setShareUrl(await shareSetlist(id))}>
            Share
          </button>
          <Link className="btn btn-primary" to={`/setlist/${id}/print`}>Print booklet</Link>
        </div>
      </header>

      {shareUrl && (
        <div className="card no-print" style={{ marginTop: 14 }}>
          <span className="mono small">{shareUrl}</span>
          <p className="muted small" style={{ margin: "6px 0 0" }}>
            Anyone with this link can open the list and reorder it.
          </p>
        </div>
      )}

      <ul className="song-list" style={{ marginTop: 20 }}>
        {items.map((item, index) => (
          <li key={item.position} className="setlist-item">
            <span className="mono muted" style={{ width: 22 }}>{index + 1}</span>
            <span className="song-row-main">
              <Link to={`/song/${item.songs?.slug}`} className="song-row-title">
                {item.songs?.title}
              </Link>
              <span className="muted small">{item.songs?.artist}</span>
            </span>

            <span className="mono small" style={{ width: 96, color: "var(--accent)" }}>
              {item.songs?.music_key ? shift(item.songs.music_key.split(" ")[0], item.transpose) : "—"}
              {item.transpose ? ` (${item.transpose > 0 ? "+" : ""}${item.transpose})` : ""}
            </span>

            <span className="no-print" style={{ display: "flex", gap: 4 }}>
              <button className="btn" onClick={() => transposeItem(item, -1)}>♭</button>
              <button className="btn" onClick={() => transposeItem(item, 1)}>♯</button>
              <button className="btn" onClick={() => move(index, -1)} disabled={index === 0}>↑</button>
              <button className="btn" onClick={() => move(index, 1)}
                      disabled={index === items.length - 1}>↓</button>
              <button className="btn" onClick={async () => {
                await removeItem(id, item.position);
                setItems(items.filter((row) => row.position !== item.position));
              }}>×</button>
            </span>
          </li>
        ))}
        {!items.length && (
          <li className="muted">Empty — open a song and add it from there.</li>
        )}
      </ul>
    </div>
  );
}
